import { useSyncExternalStore } from "react";

// Subscribe a component to a CSS media query. Re-renders only when the
// match flips. useSyncExternalStore keeps it correct under concurrent
// rendering and gives a stable server/first-paint value.
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

// Tailwind's `md` breakpoint is 768px. Below it we treat the viewport as
// "mobile" (phone / small tablet portrait) and switch to single-column +
// drawer/sheet patterns. 767.98 dodges the fractional-pixel boundary.
export function useIsMobile(): boolean {
  return useMediaQuery("(max-width: 767.98px)");
}

// True on touch / no-hover devices. Drives tap-vs-hover affordances
// (e.g. tap-to-open menus instead of hover popovers).
export function useIsTouch(): boolean {
  return useMediaQuery("(hover: none) and (pointer: coarse)");
}

// The person asked for less motion (the OS's Reduce Motion): counters jump,
// nothing slides or glows.
export function useReducedMotion(): boolean {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}
